import type { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { FactoryActionsApiClient } from './factory-actions-api-client.service';
import { factoryImportEvents } from './factory-import.events';

/**
 * Workflow store for `POST /factories/create_from_api`.
 *
 * Exposes the standard request-status signals from `withRequestStatus()`.
 * The imported factory is surfaced as the `importSucceeded` event payload so
 * downstream stores (e.g. `FactoryDetailStore`) can hydrate from it without
 * this store owning detail state.
 */
export const FactoryImportStore = signalStore(
  { providedIn: 'root' },
  withRequestStatus(),
  withReducer(
    on(factoryImportEvents.importRequested, () => ({
      requestStatus: 'loading',
    })),
    on(factoryImportEvents.importSucceeded, () => ({
      requestStatus: 'success',
    })),
    on(factoryImportEvents.importFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    }))
  ),
  withEventHandlers(
    (
      _,
      events = inject(Events),
      actions = inject(FactoryActionsApiClient)
    ) => ({
      import$: events.on(factoryImportEvents.importRequested).pipe(
        switchMap(({ payload }) =>
          actions.createFromApi(payload.payload).pipe(
            mapResponse({
              next: (factory) =>
                factoryImportEvents.importSucceeded({ factory }),
              error: (error: HttpErrorResponse) =>
                factoryImportEvents.importFailed({
                  error: resolveHttpError(error).code,
                }),
            })
          )
        )
      ),
    })
  )
);
